"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRightIcon, ListIcon, XIcon } from "@phosphor-icons/react";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

type Item = {
  id: string;
  label: string;
  url: string;
  openInNewTab: boolean;
  children: Item[];
};

function MenuLink({ item, onNavigate }: { item: Item; onNavigate?: () => void }) {
  const props = item.openInNewTab
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};

  if (!item.children.length)
    return (
      <Link href={item.url} {...props} onClick={onNavigate}>
        {item.label}
      </Link>
    );

  return (
    <details className="navigation-submenu">
      <summary>{item.label}</summary>
      <div>
        {item.children.map((child) => (
          <Link
            href={child.url}
            key={child.id}
            onClick={onNavigate}
            {...(child.openInNewTab
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            {child.label}
          </Link>
        ))}
      </div>
    </details>
  );
}

export function PublicHeader({
  siteName,
  logo,
  navigation,
  announcements,
  phone,
}: {
  siteName: string;
  logo: { id: string; altText: string } | null;
  navigation: Item[];
  announcements: Array<{
    id: string;
    title: string;
    ctaLabel: string | null;
    ctaUrl: string | null;
  }>;
  phone?: string | null;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileNavRef = useRef<HTMLDetailsElement>(null);
  const pathname = usePathname();
  const announcement = announcements[0];

  // Route değiştiğinde mobil menüyü otomatik kapat
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Dışarıya tıklandığında menüyü kapat
  useEffect(() => {
    if (!mobileOpen) return;

    function handlePointerDown(e: MouseEvent | TouchEvent) {
      if (mobileNavRef.current && !mobileNavRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMobileOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  function handleCloseMenu() {
    setMobileOpen(false);
  }

  return (
    <>
      {announcement ? (
        <aside className="announcement-bar" aria-label="Duyuru">
          <span>{announcement.title}</span>
          {announcement.ctaLabel && announcement.ctaUrl ? (
            <Link href={announcement.ctaUrl}>
              {announcement.ctaLabel} <ArrowRightIcon size={14} aria-hidden="true" />
            </Link>
          ) : null}
        </aside>
      ) : null}
      <header className="site-header">
        <div className="public-container site-header__inner">
          <Link
            className="site-logo"
            href="/"
            aria-label={`${siteName} ana sayfa`}
            onClick={handleCloseMenu}
          >
            {logo ? (
              <Image
                src={`/api/media/${logo.id}`}
                alt={logo.altText || siteName}
                width={180}
                height={48}
                unoptimized
              />
            ) : (
              <>
                <span aria-hidden="true">CV</span>
                {siteName}
              </>
            )}
          </Link>
          <nav className="desktop-navigation" aria-label="Ana navigasyon">
            {navigation.map((item) => (
              <MenuLink item={item} key={item.id} />
            ))}
          </nav>
          <div className="site-header__actions">
            {phone ? <a href={`tel:${phone}`}>{phone}</a> : null}
            <Link className="header-search-cta" href="/cruise-turlari">
              Cruise Ara <ArrowRightIcon size={16} aria-hidden="true" />
            </Link>
          </div>
          <details
            ref={mobileNavRef}
            className="mobile-navigation"
            open={mobileOpen}
            onToggle={(e) => {
              const isOpen = (e.currentTarget as HTMLDetailsElement).open;
              setMobileOpen(isOpen);
            }}
          >
            <summary
              aria-label="Ana menüyü aç veya kapat"
              onClick={(e) => {
                e.preventDefault();
                setMobileOpen((prev) => !prev);
              }}
            >
              <ListIcon size={24} aria-hidden="true" />
              <span className="sr-only">
                {mobileOpen ? "Ana menüyü kapat" : "Ana menüyü aç"}
              </span>
            </summary>
            {mobileOpen && (
              <div className="mobile-menu-panel">
                <div className="mobile-menu-panel__header">
                  <div>
                    <span>Menü</span>
                    <strong>Yolculuğunuzu keşfedin</strong>
                  </div>
                  <button
                    type="button"
                    aria-label="Menüyü kapat"
                    onClick={handleCloseMenu}
                  >
                    <XIcon size={20} />
                  </button>
                </div>
                <nav aria-label="Mobil navigasyon">
                  {navigation.map((item) => (
                    <MenuLink item={item} key={item.id} onNavigate={handleCloseMenu} />
                  ))}
                </nav>
                <div className="mobile-menu-panel__actions">
                  <Link href="/cruise-turlari" onClick={handleCloseMenu}>
                    Cruise Ara <ArrowRightIcon size={16} aria-hidden="true" />
                  </Link>
                  {phone ? (
                    <a href={`tel:${phone}`}>
                      <span>Bize ulaşın</span>
                      <strong>{phone}</strong>
                    </a>
                  ) : null}
                </div>
              </div>
            )}
          </details>
        </div>
      </header>
    </>
  );
}
